import { Inject, Injectable, InjectionToken } from '@angular/core';

import { ReplaySubject } from 'rxjs';

import { StorageKeyEnum } from '@app/core/enums/storage-key.enum';
import {
  BtrEventModel,
  BtrPassportModel,
  ClusterModel,
  CuratedCategoryModel,
  GuideModel,
  IMapSliderModel,
  PlaceModel,
  ProfileModel,
  UserModel,
} from '@app/core/models';
import { BtrStaticPageModel } from '@app/core/models/btr-static-page.model';
import { ISettingsApiModel } from '../models/api';
import { BtrPassportCheckInInfoModel } from '../models/btr-passport-check-in-info.model';

export const BROWSER_STORAGE = new InjectionToken<Storage>('Browser Storage', {
  providedIn: 'root',
  factory: (): Storage => localStorage,
});

@Injectable({ providedIn: 'root' })
export class StorageService {
  public PLACE_IDS: Array<number>;
  public profile$ = new ReplaySubject<ProfileModel | null>(1);
  public places$ = new ReplaySubject<Array<PlaceModel> | null>(1);
  public arePlacesAvailable = false;

  constructor(@Inject(BROWSER_STORAGE) public storage: Storage) {}

  // set PLACES(value: Array<PlaceModel> | null) {
  //   this.setStorageItem(StorageKeyEnum.PLACES, value);
  // }

  setPlaces(value: Array<PlaceModel> | null): void {
    this.places$.next(value);
    this.arePlacesAvailable = value !== null && value?.length > 0;
  }

  // get PLACES(): Array<PlaceModel> | null {
  //   const tmp = this.getStorageItem(StorageKeyEnum.PLACES);
  //   if (Array.isArray(tmp)) {
  //     return tmp.map(item => PlaceModel.copyObj(item));
  //   }
  //
  //   return null;
  // }

  set CLUSTERS(value: Array<ClusterModel> | null) {
    this.setStorageItem(StorageKeyEnum.CLUSTERS, value);
  }

  get CLUSTERS(): Array<ClusterModel> | null {
    const tmp = this.getStorageItem(StorageKeyEnum.CLUSTERS);
    if (Array.isArray(tmp)) {
      return tmp.map(item => ClusterModel.copyObj(item));
    }

    return null;
  }

  set COLLECTIONS(value: Array<CuratedCategoryModel> | null) {
    this.setStorageItem(StorageKeyEnum.COLLECTIONS, value);
  }

  get COLLECTIONS(): Array<CuratedCategoryModel> | null {
    const tmp = this.getStorageItem(StorageKeyEnum.COLLECTIONS);
    if (Array.isArray(tmp)) {
      return tmp.map(item => CuratedCategoryModel.copyObj(item));
    }

    return null;
  }

  set GUIDES(value: Array<GuideModel> | null) {
    this.setStorageItem(StorageKeyEnum.GUIDES, value);
  }

  get GUIDES(): Array<GuideModel> | null {
    const tmp = this.getStorageItem(StorageKeyEnum.GUIDES);
    if (Array.isArray(tmp)) {
      return tmp.map(item => GuideModel.copyObj(item));
    }

    return null;
  }

  set GUIDE(value: GuideModel | null) {
    this.setStorageItem(StorageKeyEnum.GUIDE, value);
  }

  get GUIDE(): GuideModel | null {
    const tmp = this.getStorageItem(StorageKeyEnum.GUIDE);
    if (!!tmp) {
      return GuideModel.copyObj(tmp);
    }

    return null;
  }

  set BOOKMARK_IDS(value: Array<number> | null) {
    this.setStorageItem(StorageKeyEnum.BOOKMARK_IDS, value);
  }

  get BOOKMARK_IDS(): Array<number> | null {
    return this.getStorageItem(StorageKeyEnum.BOOKMARK_IDS);
  }

  set DESTINATION_ON_SLIDER(value: Array<IMapSliderModel> | null) {
    this.setStorageItem(StorageKeyEnum.DESTINATION_ON_SLIDER, value);
  }

  get DESTINATION_ON_SLIDER(): Array<IMapSliderModel> | null {
    return this.getStorageItem(StorageKeyEnum.DESTINATION_ON_SLIDER);
  }

  set VERSION(value: string | null) {
    this.setStorageItem(StorageKeyEnum.VERSION, value);
  }

  get VERSION(): string | null {
    return this.getStorageItem(StorageKeyEnum.VERSION);
  }

  set CONFIG(value: ISettingsApiModel | null) {
    this.setStorageItem(StorageKeyEnum.CONFIG, value);
  }

  get CONFIG(): ISettingsApiModel | null {
    return this.getStorageItem(StorageKeyEnum.CONFIG);
  }

  set TOKEN(value: string | null) {
    this.setStorageItem(StorageKeyEnum.TOKEN, value);
  }

  get TOKEN(): string | null {
    return this.getStorageItem(StorageKeyEnum.TOKEN);
  }

  set USER(value: UserModel | null) {
    this.setStorageItem(StorageKeyEnum.USER, value);
  }

  get USER(): UserModel | null {
    const tmp = this.getStorageItem(StorageKeyEnum.USER);
    return tmp != null ? UserModel.copyObj(tmp) : null;
  }

  set PROFILE(value: ProfileModel | null) {
    this.profile$.next(value);
    this.setStorageItem(StorageKeyEnum.PROFILE, value);
  }

  get PROFILE(): ProfileModel | null {
    const tmp = this.getStorageItem(StorageKeyEnum.PROFILE);
    return tmp != null ? ProfileModel.copyObj(tmp) : null;
  }

  set EVENT(value: BtrEventModel | null) {
    this.setStorageItem(StorageKeyEnum.EVENT, value);
  }

  get EVENT(): BtrEventModel | null {
    const tmp = this.getStorageItem(StorageKeyEnum.EVENT);
    return tmp != null ? BtrEventModel.copyObj(tmp) : null;
  }

  set EVENTS(value: Array<BtrEventModel> | null) {
    this.setStorageItem(StorageKeyEnum.EVENTS, value);
  }

  get EVENTS(): Array<BtrEventModel> | null {
    const tmp = this.getStorageItem(StorageKeyEnum.EVENTS);
    return tmp != null ? tmp.map(item => BtrEventModel.copyObj(item)) : null;
  }

  set PASSPORT(value: BtrPassportModel | null) {
    this.setStorageItem(StorageKeyEnum.PASSPORT, value);
  }

  get PASSPORT(): BtrPassportModel | null {
    const tmp = this.getStorageItem(StorageKeyEnum.PASSPORT);
    return tmp != null ? BtrPassportModel.copyObj(tmp) : null;
  }

  set PASSPORT_CHECK_IN_INFO(value: BtrPassportCheckInInfoModel | null) {
    this.setStorageItem(StorageKeyEnum.PASSPORT_CHECK_IN_INFO, value);
  }

  get PASSPORT_CHECK_IN_INFO(): BtrPassportCheckInInfoModel | null {
    const tmp = this.getStorageItem(StorageKeyEnum.PASSPORT_CHECK_IN_INFO);
    return tmp != null ? BtrPassportCheckInInfoModel.copyObj(tmp) : null;
  }

  set STATIC_PAGES(value: Array<BtrStaticPageModel> | null) {
    this.setStorageItem(StorageKeyEnum.STATIC_PAGES, value);
  }

  get STATIC_PAGES(): Array<BtrStaticPageModel> | null {
    const tmp = this.getStorageItem(StorageKeyEnum.STATIC_PAGES);
    return tmp != null ? tmp : null;
  }

  set SELECTED_CLUSTER(value: ClusterModel | null) {
    this.setStorageItem(StorageKeyEnum.SELECTED_CLUSTER, value);
  }

  get SELECTED_CLUSTER(): ClusterModel | null {
    const tmp = this.getStorageItem(StorageKeyEnum.SELECTED_CLUSTER);
    return ClusterModel.copyObj(tmp);
  }

  set MAP_TYPE(value: string | null) {
    this.setStorageItem(StorageKeyEnum.MAP_TYPE, value);
  }

  get MAP_TYPE(): string | null {
    const tmp = this.getStorageItem(StorageKeyEnum.MAP_TYPE);
    return tmp ?? 'roadmap';
  }

  public getStorageItem(name: StorageKeyEnum): any {
    return this.jsonToObject(this.storage.getItem(`btr-${name}`));
  }

  public setStorageItem(name: StorageKeyEnum, value: any): any {
    this.storage.setItem(`btr-${name}`, this.ObjectToJson(value));
  }

  public removeKey(value: StorageKeyEnum): any {
    this.storage.removeItem(`btr-${value}`);
    this.storage.removeItem(`bm-${value}`); // remove old keys
    this.storage.removeItem(`${value}`); // remove old keys
  }

  private jsonToObject(value: any): any {
    if (value) {
      return JSON.parse(value);
    }

    return null;
  }

  private ObjectToJson(value: any): string {
    return JSON.stringify(value);
  }
}
